// Util genérico para normalizar erros de API (Axios 1.x) e exibir toast (react-toastify 11.x)
//
// Compatível com ProblemDetails / ValidationProblemDetails do ASP.NET Core.
// - Suporta { errors: { Campo: [ 'msg1', 'msg2' ] }, title, detail, status, type, traceId }
// - Cai para `message`/string quando não houver o formato acima
// - Trata erros de rede/timeout (ERR_NETWORK, ECONNABORTED)
// - Retorna mensagens normalizadas e também exibe toast de erro único

import { toast } from 'react-toastify';

/** Converte "TechnicalSpecifications" -> "Technical Specifications" */
export const formatFieldName = (field) => {
  if (!field) return '';
  const simple = String(field).split('.').slice(-1)[0];
  return simple
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
};

/** Extrai mensagens de ProblemDetails/ValidationProblemDetails */
export const parseProblemDetails = (data) => {
  const messages = [];
  if (data == null) return { messages };

  // Texto puro
  if (typeof data === 'string') {
    messages.push(data.trim());
    return { messages };
  }

  if (typeof data === 'object') {
    // ValidationProblemDetails: errors = { field: [msgs] }
    if (data.errors && typeof data.errors === 'object') {
      for (const [field, arr] of Object.entries(data.errors)) {
        if (Array.isArray(arr) && arr.length) {
          arr.forEach((m) => {
            const msg = String(m || '').trim();
            if (msg) messages.push(`${formatFieldName(field)}: ${msg}`);
          });
        }
      }
    }

    // Campos comuns de ProblemDetails
    if (data.detail) messages.push(String(data.detail).trim());
    if (!messages.length && data.title) messages.push(String(data.title).trim());
    if (!messages.length && data.message) messages.push(String(data.message).trim());

    return {
      messages,
      title: data.title,
      status: data.status,
      type: data.type,
      fields: data.errors || null,
      traceId: data.traceId,
    };
  }

  return { messages };
};

/** Normaliza AxiosError (1.x) em { status, messages[] } */
export const normalizeAxiosError = (error) => {
  let status;
  let data;

  if (error && error.response) {
    status = error.response.status;
    data = error.response.data;
  }

  const pd = parseProblemDetails(data);

  // Rede/timeout: quando não há response
  let networkMessage;
  if (!error?.response) {
    if (error?.code === 'ECONNABORTED') {
      networkMessage = 'Tempo de requisição esgotado. Tente novamente.';
    } else {
      networkMessage = 'Falha de rede. Verifique sua conexão e tente novamente.';
    }
  }

  const collected = [];
  if (networkMessage) collected.push(networkMessage);
  if (pd.messages?.length) collected.push(...pd.messages);
  if (!collected.length && error?.message) collected.push(String(error.message));

  // Dedup simples
  const messages = Array.from(new Set(collected.filter(Boolean).map((s) => s.trim())));

  return { status, messages };
};

/** Mostra um único toast de erro com todas as mensagens normalizadas */
export const toastApiError = (error, options = {}) => {
  const { messages } = normalizeAxiosError(error);
  const fallback = options.fallbackMessage || 'Ocorreu um erro ao processar sua solicitação.';

  // Se vier mais de uma mensagem, junta de forma legível
  const text =
    messages.length > 1 ? messages.map((m) => `• ${m}`).join('  |  ') : messages[0] || fallback;

  toast.error(text, {
    // ajuste aqui se quiser exibir mais tempo
    autoClose: 7000,
  });

  return messages; // útil em testes ou logs
};

export default toastApiError;
